/* assignmentSetup.js - WHY a bowl surface is read-only, said truthfully.
 *
 * V4.001 (backend d433714) made the Manage Assignments body roster
 * authoritative: `overlayForMap({ requirePresent: true })` now refuses to fall
 * back to the breakdown when the `assignment_body_roster` row is missing. Until
 * a human loads that roster, the bowl board, team actions and the committee
 * book all come back with `ready: false` and this error:
 *
 *   "assignment roster unavailable: Authoritative assignment_body_roster row
 *    missing; a human-managed roster must exist before a bowl pick"
 *
 * The screens were saying "history unavailable" for it. That is wrong in a way
 * that matters: it tells a rep their saved work may be gone, and it tells an
 * exec to go looking at history when the fix is assignment setup. A real
 * history read failure (the committee ledger, the picks ledger, the board) is a
 * DIFFERENT error and still says "history unavailable".
 *
 * Pure: no React, no fetch. The match is pinned by assignmentSetup.test.js to
 * the exact live string, so a backend rewording fails a test instead of
 * silently falling back to the old banner.
 */

/** The live V4.001 refusal for a missing roster row. Not "invalid", not "unparseable". */
export const ROSTER_MISSING = /authoritative assignment_body_roster row missing/i

/** Where an exec fixes it: Command Center > Governing Bodies (BodyRoster). */
const SETUP_PATH = '/governing-bodies'
export const SETUP_HREF = assignmentSetupHref('', '/bowl-eligibility')

/** Read-only navigation. Never treats a conference link as a roster grant. */
export function assignmentSetupHref(conference = '', returnTo = '') {
  const params = new URLSearchParams({ level: 'college', setup: 'assignments' })
  if (conference) params.set('body', conference)
  if (bowlReturnHref(returnTo)) params.set('returnTo', returnTo)
  return `${SETUP_PATH}?${params}`
}

export function bowlReturnHref(value) {
  return typeof value === 'string' && /^\/bowl-eligibility(?:\?[^#]*)?$/.test(value) ? value : ''
}

/* ⚠️ EXACT STRINGS, NOT A SUBSTRING (Dot's review of 390d574). The regex above
 * also matched "missing roster + a pick-read timeout" written as one sentence,
 * and any uncertain error that merely QUOTED the phrase, so a real read failure
 * could be shown as "setup required". Only the exact strings the backend
 * records for the confirmed missing row are trusted, plus its structured flag. */
const MISSING_MESSAGE = 'Authoritative assignment_body_roster row missing; a human-managed roster must exist before a bowl pick'
const MISSING_EXACT = new Set([
  MISSING_MESSAGE,
  'assignment roster unavailable: ' + MISSING_MESSAGE, // deskContext (workMeta / deskMeta / committeeMeta)
  'Current desk roster unavailable: ' + MISSING_MESSAGE, // committee decision read
])
const isRosterMissing = (e) => {
  if (!e) return false
  if (typeof e === 'string') return MISSING_EXACT.has(e.trim())
  if (typeof e === 'object') {
    if (e.code !== 'assignment_roster_missing') return false
    // A structured code cannot erase a contradictory failure description.
    return ['detail', 'message'].every((key) => e[key] == null || e[key] === ''
      || typeof e[key] === 'string' && MISSING_EXACT.has(e[key].trim()))
  }
  return false
}

/**
 * Classify a surface's read metadata (`workMeta`, `committeeMeta`, `deskMeta`).
 *
 * Returns { state, rosterMissing, otherErrors }, where state is one of:
 *   'ready'        the read is current; nothing to say
 *   'setup'        the ONLY problem is the missing assignment roster
 *   'pending'      an operation is in flight
 *   'unavailable'  a real read failure, or no current read at all
 *
 * A missing roster never hides a real failure: if any other error is present
 * the state is 'unavailable' and `rosterMissing` is still reported, so the
 * surface can say both.
 */
export function readinessOf(meta, { requireVersion = false } = {}) {
  if (!meta || typeof meta !== 'object') return { state: 'unavailable', rosterMissing: false, otherErrors: [] }
  const errors = Array.isArray(meta.errors) ? meta.errors
    : meta.errors == null ? [] : ['Current read errors have an invalid shape.']
  const rosterMissing = meta.setupRequired === true || meta.setupRequired === 'assignment_roster' || errors.some(isRosterMissing)
  const otherErrors = errors.filter((e) => !isRosterMissing(e)).map((e) => (typeof e === 'string' ? e : String(e?.detail || e?.message || e?.code || 'unknown error')))
  const pending = Array.isArray(meta.pending) && meta.pending.length > 0 || isUncertainOutcome({ body: meta })
  if (meta.pending != null && !Array.isArray(meta.pending)) otherErrors.push('Current pending-operation history has an invalid shape.')
  if (otherErrors.length) return { state: 'unavailable', rosterMissing, otherErrors, pending }
  if (pending) return { state: 'pending', rosterMissing, otherErrors, pending }
  if (rosterMissing) return { state: 'setup', rosterMissing, otherErrors }
  if (meta.ready === true && (!requireVersion || Number.isSafeInteger(meta.version) && meta.version >= 0)) return { state: 'ready', rosterMissing, otherErrors }
  return { state: 'unavailable', rosterMissing, otherErrors }
}

/**
 * Is a REFUSED request the backend's "assignment setup required" answer?
 * Backend fix/roster-setup-409-20261026: 409 { error: 'assignment_setup_required',
 * setupRequired: 'assignment_roster' } instead of the old 500.
 */
export function isSetupRefusal(e) {
  const b = e?.body
  if (!b || typeof b !== 'object') return false
  /* Only the clean, pre-effect refusal: never an uncertain or reconciliation
   * outcome, never one the server marked unsafe to retry, never a stray field. */
  if (isUncertainOutcome(e)) return false
  if (e?.status !== 409 || b.readFailure === true || hasOtherErrors(b)) return false
  return b.error === 'assignment_setup_required' && b.retrySafe === true
}

const hasOtherErrors = (body) => body.errors != null
  && (!Array.isArray(body.errors) || body.errors.length > 0)

/** The server could not confirm what happened: a pending or uncertain write. */
export function isUncertainOutcome(e) {
  const b = e?.body
  if (!b || typeof b !== 'object') return false
  const code = String(b.error || e?.code || '')
  return b.uncertain === true || b.retrySafe === false || b.reconciliationRequired === true
    || /pending|uncertain|in_progress/.test(code)
    || b.pendingOperation != null
    || b.mayHaveWritten != null && (!Array.isArray(b.mayHaveWritten) || b.mayHaveWritten.length > 0)
    || b.recordsTouched === null || b.recordsTouched != null && b.recordsTouched !== 0
}

/** A read failed BEFORE anything was written (timeout, outage): retry the read. Never setup. */
export function isReadFailure(e, { forMutation = false } = {}) {
  const b = e?.body
  if (!b || typeof b !== 'object' || isUncertainOutcome(e)) return false
  // Only this server pre-effect contract can release a mutation fence. A GET
  // may offer another read without retrySafe, but never another write.
  return e?.status === 503 && b.error === 'desk_context_unavailable'
    && b.readFailure === true && b.retryable === true && !hasOtherErrors(b)
    && (forMutation ? b.retrySafe === true : b.retrySafe !== false)
}

/** One answer per refused action: 'uncertain' | 'read_failure' | 'setup' | 'refused'. Order matters. */
export function classifyActionError(e) {
  if (isUncertainOutcome(e)) return 'uncertain'
  if (isReadFailure(e, { forMutation: true })) return 'read_failure'
  if (isSetupRefusal(e)) return 'setup'
  return 'refused'
}

/** The worklist (GET work-orders): 'setup' | 'read_failure' | 'error'. */
export function workOrdersReadState(e) {
  if (isSetupRefusal(e)) return 'setup'
  if (isReadFailure(e)) return 'read_failure'
  return 'error'
}

export const READ_FAILURE_COPY = Object.freeze({
  worklist: 'The bowl work could not be read just now (a temporary read failure, not a setup problem). Try again in a moment.',
  action: 'The current state could not be read. Nothing was saved. This is a temporary read failure, not a setup problem. Try again in a moment.',
})

/**
 * "Needs reconciliation", with what may have happened, from the server's
 * uncertain-outcome body. Never says "nothing was sent" and never the setup
 * sentence: the honest answer is that part of the action may have landed.
 */
export function reconciliationOf(e) {
  const b = e?.body || {}
  const may = Array.isArray(b.mayHaveWritten) ? b.mayHaveWritten : []
  const items = may.map((x) => {
    const label = String(x?.label || x?.what || 'an unknown write')
    return label + (x?.contactId ? ` (contact ${x.contactId})` : '') + (x?.draftId ? ` (draft ${x.draftId})` : '')
  })
  const requestId = b.requestId || b.pendingOperation?.requestId || null
  const what = items.length ? `What may have happened: ${items.join('; ')}.` : 'The server could not confirm what happened.'
  const text = `Needs reconciliation. The action outcome is uncertain: it may have partly happened. Do not repeat it. ${what}`
    + ` An executive should reconcile it against HubSpot and the saved history${requestId ? ` (request ${requestId})` : ''} before anyone acts on this team again.`
  return { text, items, requestId, cause: b.cause || null }
}

/** The words, in one place, so the team row, the committee book and the board agree. */
export const SETUP_COPY = Object.freeze({
  title: 'Assignment setup required.',
  exec: 'An executive must finish Manage Assignments setup. A current history read and existing action permissions are still required.',
  rep: 'Changes are paused until an executive finishes assignment setup and the current history can be read.',
  cta: 'Set up assignments',
})

/** Navigation chooses a unique exact/former registry match, never an assignment. */
export function assignmentCardChoice(hits = []) {
  const exact = hits.filter(hit => hit.tier === 'exact' || hit.tier === 'former')
  const choices = exact.length ? exact : hits
  return { selected: choices.length === 1 ? choices[0].entity : null, choices: choices.map(hit => hit.entity) }
}
